import React, { createContext, useLayoutEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { Theme } from '../types/theme';
import { getTheme, setTheme } from '../utils/themeStorage';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

function syncTheme(theme: Theme) {
  const root = document.documentElement;
  const body = document.body;

  root.classList.remove('theme-dark', 'theme-vir');
  body.classList.remove('theme-dark', 'theme-vir');

  if (theme === 'dark') {
    root.classList.add('theme-dark');
    body.classList.add('theme-dark');
  } else if (theme === 'vir') {
    root.classList.add('theme-vir');
    body.classList.add('theme-vir');
  }

  root.style.colorScheme = theme === 'dark' ? 'dark' : 'light';

  const themeColor = theme === 'dark' ? '#090D16' : theme === 'vir' ? '#FCF8F5' : '#F7F9FB';
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute('content', themeColor);
  }
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(() => getTheme());

  useLayoutEffect(() => {
    syncTheme(theme);
    setTheme(theme);
  }, [theme]);

  const toggleTheme = () => {
    const nextTheme = (() => {
      switch (theme) {
        case 'light':
          return 'dark';
        case 'dark':
          return 'vir';
        case 'vir':
          return 'light';
        default:
          return 'light';
      }
    })();

    const startTransition = () => {
      setThemeState(nextTheme);
    };

    // Check if browser supports View Transitions and user doesn't prefer reduced motion
    const hasViewTransition =
      typeof document !== 'undefined' &&
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (document as any).startViewTransition !== undefined &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!hasViewTransition) {
      startTransition();
      return;
    }

    // Disable CSS transitions on elements to avoid visual jank during screenshotting
    document.documentElement.classList.add('disable-transitions');

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const transition = (document as any).startViewTransition(() => {
      flushSync(() => {
        startTransition();
      });
    });

    transition.finished.finally(() => {
      document.documentElement.classList.remove('disable-transitions');
    });
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
